// Push notifications via @capacitor/push-notifications.
//
// Flow:
//   1. Caller (App.tsx) invokes initPush() once a user is authenticated.
//   2. We check the OS permission state, request if unknown, then call
//      PushNotifications.register() to start APNs/FCM enrollment.
//   3. The plugin fires 'registration' with the device token; we POST it
//      to /api/push/register so the worker can target this device.
//   4. Foreground / tap handlers route deep-links into the SPA via the
//      same 'yelo:deeplink' custom event used by appUrlOpen.
//
// Web is a no-op. Android is a no-op for v1 (FCM lands later).

import { Capacitor } from "@capacitor/core";
import { api } from "../api";

let registered = false;
let lastToken: string | null = null;

const platform = (): "ios" | "android" | "web" => Capacitor.getPlatform() as any;

// native-only — imported lazily so @capacitor/app stays out of the web bundle.
const capApp = async () => (await import("@capacitor/app")).App;

const isAppStoreBuild = async (): Promise<boolean> => {
  try {
    const info = await (await capApp()).getInfo();
    return !!info && !info.id?.endsWith(".dev");
  } catch {
    return true;
  }
};

const sendTokenToServer = async (token: string): Promise<void> => {
  try {
    const info = await (await capApp()).getInfo().catch(() => null);
    const apnsEnv = (await isAppStoreBuild()) ? "production" : "sandbox";
    await api("/push/register", {
      method: "POST",
      body: JSON.stringify({
        token,
        platform: platform(),
        appBuild: info?.build ?? null,
        apnsEnv,
      }),
    });
    lastToken = token;
    console.log("[push] token registered");
  } catch (e: any) {
    console.log("[push] register failed", e?.message ?? e);
  }
};

export const initPush = async (): Promise<void> => {
  if (registered) return;
  if (!Capacitor.isNativePlatform()) return;
  if (platform() !== "ios") return; // android wires up when FCM lands

  let mod: any;
  try {
    mod = await import("@capacitor/push-notifications");
  } catch (e: any) {
    console.log("[push] plugin missing", e?.message ?? e);
    return;
  }
  const PN = mod.PushNotifications;

  try {
    let perms = await PN.checkPermissions();
    if (perms.receive !== "granted" && perms.receive !== "denied") {
      perms = await PN.requestPermissions();
    }
    if (perms.receive !== "granted") {
      console.log("[push] permission not granted:", perms.receive);
      return;
    }

    PN.addListener("registration", (token: { value: string }) => {
      if (token.value && token.value !== lastToken) sendTokenToServer(token.value);
    });
    PN.addListener("registrationError", (err: { error: string }) => {
      console.log("[push] registrationError", err.error);
    });
    PN.addListener("pushNotificationReceived", (n: any) => {
      console.log("[push] foreground", n?.data?.kind ?? "(no kind)");
    });
    PN.addListener("pushNotificationActionPerformed", (action: any) => {
      const data = action?.notification?.data ?? {};
      if ((data.kind === "chain_punched" || data.kind === "punch_validated" || data.kind === "chain_warning") && data.chainId) {
        window.dispatchEvent(new CustomEvent("yelo:deeplink", {
          detail: { kind: "chain", value: data.chainId },
        }));
      } else if (data.kind === "chain_died") {
        window.dispatchEvent(new CustomEvent("yelo:deeplink", {
          detail: { kind: "punch_new", value: data.rematchHandle ?? "" },
        }));
      }
    });

    await PN.register();
    registered = true;
  } catch (e: any) {
    console.log("[push] init failed", e?.message ?? e);
  }
};
